// src/pages/draft_document/DraftDocumentList.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./DraftDocumentList.module.css";
import api from "../../api";
import { YONGYEOK_CLAIM_SITE_KEYS, YONGYEOK_SUBMIT_SITE_KEYS } from "../../components/document-type/ServiceGiseongAutoFill";
import { internalDraftNoOf as displayDraftNo } from "../../utils/resolutionDraft";

const DOC_TYPE_LABEL = {
  income_draft: "수입기안",
  expense_draft: "지출기안",
  income_resolution: "수입결의",
  expense_resolution: "지출결의",
  leave_request: "휴가신청서",
};

export default function DraftDocumentList() {
  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);  // ✅ 삭제 중 ID 추적
  const [filters, setFilters] = useState({
    document_type: "",
    status: "",
    search: "",
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    fetchDocuments();
  }, [filters]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.document_type) params.append("document_type", filters.document_type);
      if (filters.status) params.append("status", filters.status);
      if (filters.search) params.append("search", filters.search);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);

      const { data } = await api.get(`/draft/documents/?${params.toString()}`);
      setDocuments(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("❌ 목록 불러오기 실패:", err);
      alert("목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleViewDetail = (id, view) => {
    navigate(view ? `/approval/draftdocument/${id}?view=${view}` : `/approval/draftdocument/${id}`);
  };

  const handleCreateNew = () => {
    // ✅ 문서종류별 분리 페이지로 직접 진입(지출기안)
    navigate("/approval/expense-draft/new");
  };

  // ✅ 수정: DraftDocumentNew에 id를 넘겨 기존 데이터 불러오기
  const handleEdit = (e, doc) => {
    e.stopPropagation();
    if (doc.status === "pending") {
      alert("결재 진행 중인 문서는 수정할 수 없습니다.");
      return;
    }
    if (doc.status === "approved") {
      alert("이미 승인된 문서는 수정할 수 없습니다.");
      return;
    }
    navigate(`/approval/draftdocument/${doc.id}/edit`);
  };

  // ✅ 삭제
  const handleDelete = async (e, doc) => {
    e.stopPropagation();

    if (doc.status === "pending") {
      alert("결재 진행 중인 문서는 삭제할 수 없습니다.");
      return;
    }
    if (doc.status === "approved") {
      alert("이미 승인된 문서는 삭제할 수 없습니다.");
      return;
    }

    if (!window.confirm(`"${doc.title}" 기안서를 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`)) return;

    setDeletingId(doc.id);
    try {
      await api.delete(`/draft/documents/${doc.id}/`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err) {
      console.error("❌ 삭제 실패:", err);
      alert("삭제 중 오류가 발생했습니다. 관리자에게 문의하세요.");
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft:    { text: "작성중", cls: styles.badgeDraft },
      pending:  { text: "결재중", cls: styles.badgePending },
      approved: { text: "승인",   cls: styles.badgeApproved },
      rejected: { text: "반려",   cls: styles.badgeRejected },
    };
    return badges[status] || { text: status, cls: styles.badgeDraft };
  };

  // ✅ 수정/삭제 가능 여부
  const isEditable = (status) => status === "draft" || status === "rejected";

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>📋 기안서 관리</h2>
        <button className={styles.createBtn} onClick={handleCreateNew}>
          ➕ 새 기안 작성
        </button>
      </div>

      {/* 필터 영역 */}
      <div className={styles.filters}>
        <div className={styles.filterRow}>
          <select
            value={filters.document_type}
            onChange={(e) => handleFilterChange("document_type", e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">전체 문서 종류</option>
            <option value="income_draft">수입기안</option>
            <option value="expense_draft">지출기안</option>
            <option value="income_resolution">수입결의서</option>
            <option value="expense_resolution">지출결의서</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">전체 상태</option>
            <option value="draft">작성중</option>
            <option value="pending">결재중</option>
            <option value="approved">승인</option>
            <option value="rejected">반려</option>
          </select>

          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => handleFilterChange("start_date", e.target.value)}
            className={styles.filterDate}
          />
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => handleFilterChange("end_date", e.target.value)}
            className={styles.filterDate}
          />

          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            className={styles.filterSearch}
            placeholder="제목, 기안번호, 거래처 검색..."
          />
        </div>
      </div>

      {/* 테이블 */}
      <div className={styles.tableWrapper}>
        {loading ? (
          <div className={styles.loading}>불러오는 중...</div>
        ) : documents.length === 0 ? (
          <div className={styles.empty}>기안서가 없습니다.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>번호</th>
                <th>문서 종류</th>
                <th>상태</th>
                <th>기안번호</th>
                <th>제목</th>
                <th>거래처</th>
                <th>금액</th>
                <th>작성일</th>
                <th>작성자</th>
                <th>결재현황</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc, idx) => {
                const statusBadge = getStatusBadge(doc.status);
                const editable = isEditable(doc.status);
                const isDeleting = deletingId === doc.id;
                const rowNumber = documents.length - idx;

                // ✅ 용역 수입기안 청구형(공단 계약)/제출형(기성계 제출) — 내부기안/공문 2행으로 전개
                const isYongyeokClaim = YONGYEOK_CLAIM_SITE_KEYS.includes(doc.template_code);
                const isYongyeokPaired = isYongyeokClaim || YONGYEOK_SUBMIT_SITE_KEYS.includes(doc.template_code);
                const claimRows = isYongyeokPaired
                  ? [
                      { view: "internal", label: "내부기안", draftNo: doc.draft_no, badgeCls: styles.badgeDraft },
                      { view: "official", label: "공문", draftNo: doc.template_data?.officialDraftNo || "", badgeCls: styles.badgeApproved },
                    ]
                  : [null];

                // ✅ 관리 버튼(수정/삭제)은 문서 1건당 1번만 — 공문 행에서는 표시하지 않음
                const renderActions = (view) => (
                  <td onClick={(e) => e.stopPropagation()}>
                    {(!isYongyeokPaired || view === "internal") && (
                      <div className={styles.actionGroup}>
                        {/* 상세보기 */}
                        <button
                          className={styles.viewBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetail(doc.id, view);
                          }}
                        >
                          🔍
                        </button>

                        {/* 수정 */}
                        <button
                          className={`${styles.editBtn} ${!editable ? styles.disabledBtn : ""}`}
                          onClick={(e) => handleEdit(e, doc)}
                          disabled={!editable}
                          title={editable ? "수정" : "결재중/승인된 문서는 수정 불가"}
                        >
                          ✏️
                        </button>

                        {/* 삭제 */}
                        <button
                          className={`${styles.deleteBtn} ${!editable ? styles.disabledBtn : ""}`}
                          onClick={(e) => handleDelete(e, doc)}
                          disabled={!editable || isDeleting}
                          title={editable ? "삭제" : "결재중/승인된 문서는 삭제 불가"}
                        >
                          {isDeleting ? "⏳" : "🗑️"}
                        </button>
                      </div>
                    )}
                  </td>
                );

                return claimRows.map((claimRow) => (
                  <tr
                    key={claimRow ? `${doc.id}-${claimRow.view}` : doc.id}
                    className={styles.clickableRow}
                    onClick={() => handleViewDetail(doc.id, claimRow?.view)}
                  >
                    <td>{rowNumber}</td>

                    <td>
                      <span className={styles.docTypeTag}>
                        {DOC_TYPE_LABEL[doc.document_type] || doc.document_type_display}
                      </span>
                      {claimRow && (
                        <span className={`${styles.badge} ${claimRow.badgeCls}`} style={{ marginLeft: 4 }}>
                          {claimRow.label}
                        </span>
                      )}
                    </td>

                    <td>
                      <span className={`${styles.badge} ${statusBadge.cls}`}>
                        {statusBadge.text}
                      </span>
                    </td>

                    <td className={styles.draftNo}>{claimRow ? claimRow.draftNo : displayDraftNo(doc)}</td>
                    <td className={styles.title}>{doc.title}</td>
                    <td>{doc.partner_name}</td>

                    <td className={styles.amount}>
                      {Number(doc.amount).toLocaleString()}원
                    </td>

                    <td>{doc.draft_date}</td>
                    <td>{doc.created_by_name}</td>

                    <td>
                      <span className={styles.approvalBadge}>
                        {doc.approval_summary?.approved || 0}/
                        {doc.approval_summary?.total || 0}
                      </span>
                    </td>

                    {renderActions(claimRow?.view)}
                  </tr>
                ));
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}